import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Flex, Box } from '@rebass/grid';
import { Formik, Field } from 'formik';

import { getThemeProp } from '@amtrav/themes';
import { IconNext } from '@amtrav/icons';
import { TextInput, CityAutocomplete, Button } from '@amtrav/view-components';
import { validatePassword, invalidPwMessage } from '@amtrav/password-validator';
import { findLocations } from '@amtrav/webservice';
import { getTokenValue, THEME_TOKENS } from '@amtrav/theme-system';
import SelectBox from './SelectBox';

const NextPrevButtons = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  min-height: 105px;
`;

const PrevButton = styled.button`
  font-size: 20px;
  font-family: inherit;
  font-weight: 600;
  color: ${getThemeProp('global.link--color')};
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    min-height: 60px;
  }
`;

const Label = styled.label`
  position: relative;
  left: 3px;
  top: -4px;
  font-weight: 600;
`;

const AddressInput = styled.div`
  z-index: 100;
`;

const ErrorMessageContent = styled.div`
  min-height: 12px;
  max-height: 42px;
  width: 100%;
  margin-top: 4px;
`;

const ErrorMessage = styled.div`
  font-size: 12px;
  color: ${getThemeProp('global.danger--color')};
`;

// TODO: get these from the theme without using a styled-component
const a2bDangerColor = getTokenValue(THEME_TOKENS.base.color.danger);
const a2bBorderColor = getTokenValue(THEME_TOKENS.textInput.color.border);

const validatePasswordHandler = (value) => {
  const result = validatePassword(value);
  return result.isValid ? '' : invalidPwMessage;
};

const passwordMatch = (value, check) => {
  if (value !== check) {
    return 'Password and Confirm Password must match';
  }
  return '';
};

// WrappedTextInput ties our TextInput to a Formik Field
const WrappedTextInput = ({ name, label, errorMsg, touched, validator, type, onBlur }) => {
  const showError = touched && errorMsg;
  return (
    <Field name={name} validate={validator}>
      {({ field, form }) => {
        const { validateOnBlur } = form;
        if (!onBlur && !validateOnBlur && process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn(
            `ValidationOnBlur is disabled and no onBlur prop is set for ${name}. Validation will not execute.`,
          );
        }
        return (
          <>
            <Label>{label}</Label>
            <TextInput
              message={showError ? errorMsg : null}
              validationState={showError ? 'Validation Error' : ''}
              kind={showError ? 'danger' : null}
              name={name}
              type={type}
              {...field}
              onBlur={onBlur}
              onChange={field.onChange}
            />
            <ErrorMessageContent>{showError && <ErrorMessage>{errorMsg}</ErrorMessage>}</ErrorMessageContent>
          </>
        );
      }}
    </Field>
  );
};

WrappedTextInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  errorMsg: PropTypes.string,
  touched: PropTypes.bool,
  validator: PropTypes.func,
  type: PropTypes.string,
  onBlur: PropTypes.func,
};

WrappedTextInput.defaultProps = {
  errorMsg: null,
  touched: false,
  validator: () => {},
  type: 'text',
  onBlur: undefined,
};

// WrappedSelectInput ties a select input to a Formik Field
const WrappedSelectInput = ({ name, options, label, errorMsg, touched, validator, onBlur }) => {
  const showError = touched && errorMsg;
  return (
    <Field name={name} validate={validator}>
      {({ field, form }) => {
        const { validateOnBlur } = form;
        if (!onBlur && !validateOnBlur && process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn(
            `ValidationOnBlur is disabled and no onBlur prop is set for ${name}. Validation will not execute.`,
          );
        }
        return (
          <>
            <Label>{label}</Label>
            <SelectBox
              borderColor={errorMsg ? a2bDangerColor : a2bBorderColor}
              message={showError ? errorMsg : null}
              name={name}
              {...field}
              onBlur={onBlur}
              onChange={(obj) => {
                const val = options[obj.target.options.selectedIndex].value;
                form.setFieldValue(name, val);
              }}>
              <>
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.text}
                  </option>
                ))}
              </>
            </SelectBox>
            <ErrorMessageContent>{errorMsg ? <ErrorMessage>{errorMsg}</ErrorMessage> : null}</ErrorMessageContent>
          </>
        );
      }}
    </Field>
  );
};

WrappedSelectInput.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  label: PropTypes.string.isRequired,
  errorMsg: PropTypes.string,
  touched: PropTypes.bool,
  validator: PropTypes.func,
  type: PropTypes.string,
  onBlur: PropTypes.func,
};

WrappedSelectInput.defaultProps = {
  errorMsg: null,
  options: [],
  touched: false,
  validator: () => {},
  type: 'text',
  onBlur: undefined,
};

//
// CompanyForm component
//
const CompanyForm = ({ initialValues, onSubmit, validator, enableReinitialize }) => {
  const [locations, setLocations] = useState([]);
  const inputChangeHandler = (val) => {
    if (val) {
      findLocations(val, 'flights').then((res) => setLocations(res));
    } else {
      setLocations([]);
    }
  };
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize={enableReinitialize}
      validateOnChange={false}
      validateOnBlur={false}>
      {({
        errors,
        touched,
        // values,
        // isSubmitting,
        handleSubmit,
        handleBlur,
        status,
        validateField,
        setFieldValue,
        // ...rest,
      }) => {
        const onBlurOverride = (event) => {
          const { name } = event.target;
          handleBlur(event);
          validateField(name);
        };
        return (
          <form onSubmit={handleSubmit}>
            {status && status.message ? <h2>{status.message}</h2> : null}
            <Flex flexWrap="wrap">
              <Box width={[1, 1 / 2]} pr={[0, 2]} mb={[2, 3, 3]}>
                <WrappedTextInput
                  name="companyName"
                  label="Company Name"
                  errorMsg={errors.companyName}
                  touched={touched.companyName}
                  onBlur={onBlurOverride}
                  validator={(value) => validator.validate('companyName', { companyName: value })}
                />
              </Box>
              <Box width={[1, 1 / 2]} pr={[0, 2]} mb={[2, 3, 3]}>
                <Label>Company Address</Label>
                <Field
                  name="companyAddress"
                  validate={(value) =>
                    validator.validate('companyAddress', {
                      companyAddress: value,
                    })
                  }>
                  {({ field }) => (
                    <AddressInput>
                      <CityAutocomplete
                        placeholder=""
                        inputValue={field.value}
                        items={locations}
                        defaultSelectedItem={{
                          Display: field.value || '',
                          LocID: '',
                        }}
                        onInputChange={(value) => {
                          if (value !== undefined) {
                            setFieldValue('companyAddress', value);
                            inputChangeHandler(value);
                          }
                        }}
                        onSelectionChange={(selection) => {
                          setFieldValue('companyAddress', selection.Display);
                        }}
                        suggestionsCount={8}
                      />
                      <ErrorMessageContent>
                        {field.value ? null : <ErrorMessage>{errors.companyAddress}</ErrorMessage>}
                      </ErrorMessageContent>
                    </AddressInput>
                  )}
                </Field>
              </Box>
              <Box width={[1, 1 / 2]} pr={[0, 2]} mb={[2, 3, 3]}>
                <WrappedTextInput
                  name="companyWebsite"
                  label="Company Website"
                  errorMsg={errors.companyWebsite}
                  touched={touched.companyWebsite}
                  onBlur={onBlurOverride}
                  validator={(value) =>
                    validator.validate('companyWebsite', {
                      companyWebsite: value,
                    })
                  }
                />
              </Box>
              <Box width={[1, 1 / 2]} pr={[0, 2]} mb={[2, 3, 3]}>
                <WrappedTextInput
                  name="companyIndustry"
                  label="Industry"
                  errorMsg={errors.companyIndustry}
                  touched={touched.companyIndustry}
                  onBlur={onBlurOverride}
                  validator={(value) =>
                    validator.validate('companyIndustry', {
                      companyIndustry: value,
                    })
                  }
                />
              </Box>
              <Box width={[1, 1 / 2]} pr={[0, 2]} mb={[2, 3, 3]}>
                <WrappedSelectInput
                  name="estimatedNumberOfTravelers"
                  validator={(value) =>
                    validator.validate('estimatedNumberOfTravelers', {
                      estimatedNumberOfTravelers: value,
                    })
                  }
                  label="Estimated # of Travelers"
                  options={[
                    {
                      value: '',
                      text: 'Choose option...',
                    },
                    {
                      value: 1,
                      text: 'Less than 5',
                    },
                    {
                      value: 5,
                      text: '5-25',
                    },
                    {
                      value: 25,
                      text: '25-50',
                    },
                    {
                      value: 50,
                      text: '50-100',
                    },
                    {
                      value: 100,
                      text: '100-500',
                    },
                    {
                      value: 500,
                      text: '500-1000',
                    },
                    {
                      value: 1000,
                      text: 'More than 1000',
                    },
                  ]}
                  errorMsg={errors.estimatedNumberOfTravelers}
                  onBlur={onBlurOverride}
                />
              </Box>
              <Box width={[1, 1 / 2]} pr={[0, 2]} mb={[2, 3, 3]}>
                <WrappedSelectInput
                  name="estimatedAnnualTravelBudget"
                  validator={(value) =>
                    validator.validate('estimatedAnnualTravelBudget', {
                      estimatedAnnualTravelBudget: value,
                    })
                  }
                  label="Estimated Travel Budget"
                  options={[
                    {
                      value: '',
                      text: 'Choose option...',
                    },
                    {
                      value: 50000,
                      text: 'Under $100K',
                    },
                    {
                      value: 100000,
                      text: '$100K - $500K',
                    },
                    {
                      value: 500000,
                      text: '$500K - $1M',
                    },
                    {
                      value: 1000000,
                      text: '$1M-$5M',
                    },
                    {
                      value: 5000000,
                      text: 'More than $5M',
                    },
                  ]}
                  errorMsg={errors.estimatedAnnualTravelBudget}
                  onBlur={onBlurOverride}
                />
              </Box>
            </Flex>
            <NextPrevButtons>
              <Button type="submit" text="Next" size="large" iconSuffix={<IconNext />} />
            </NextPrevButtons>
          </form>
        );
      }}
    </Formik>
  );
};

CompanyForm.propTypes = {
  initialValues: PropTypes.shape({}), // TODO: fill out the shape
  onSubmit: PropTypes.func,
  validator: PropTypes.shape({
    validate: PropTypes.func,
  }),
  enableReinitialize: PropTypes.bool,
};

CompanyForm.defaultProps = {
  initialValues: {},
  onSubmit: () => {},
  validator: { validate: () => {} },
  enableReinitialize: true,
};

//
// ProfileForm component
//
const ProfileForm = ({ initialValues, onSubmit, onBack, validator, enableReinitialize }) => {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize={enableReinitialize}
      validateOnChange={false}
      validateOnBlur={false}>
      {({ errors, touched, values, isSubmitting, handleSubmit, handleBlur, status, validateField }) => {
        const onBlurOverride = (event) => {
          const { name } = event.target;
          handleBlur(event);
          validateField(name);
        };
        return (
          <form onSubmit={handleSubmit}>
            {status && status.message ? <h2>{status.message}</h2> : null}
            <Flex flexWrap="wrap">
              <Box width={[1, '29%']} pr={[0, 2]} mb={[2, 3, 3]}>
                <WrappedTextInput
                  name="firstName"
                  label="First Name"
                  errorMsg={errors.firstName}
                  touched={touched.firstName}
                  onBlur={onBlurOverride}
                  validator={(value) => validator.validate('firstName', { firstName: value })}
                />
              </Box>

              <Box width={[1, '29%']} px={[0, 2]} mb={[2, 3, 3]}>
                <WrappedTextInput
                  name="middleName"
                  label="Middle Name"
                  errorMsg={errors.middleName}
                  touched={touched.middleName}
                  onBlur={onBlurOverride}
                  validator={(value) => validator.validate('middleName', { middleName: value })}
                />
              </Box>

              <Box width={[1, '29%']} px={[0, 2]} mb={[2, 3, 3]}>
                <WrappedTextInput
                  name="lastName"
                  label="Last Name"
                  errorMsg={errors.lastName}
                  touched={touched.lastName}
                  onBlur={onBlurOverride}
                  validator={(value) => validator.validate('lastName', { lastName: value })}
                />
              </Box>

              <Box width={[1, '13%']} pl={[0, 2]} mb={[2, 3, 3]}>
                <WrappedSelectInput
                  name="suffixName"
                  validator={(value) => validator.validate('suffixName', { suffixName: value })}
                  label="Suffix"
                  options={[
                    { text: 'None', value: '' },
                    { text: 'Jr', value: 'Jr' },
                    { text: 'Sr', value: 'Sr' },
                    { text: 'II', value: 'II' },
                    { text: 'III', value: 'III' },
                    { text: 'IV', value: 'IV' },
                  ]}
                  onBlur={onBlurOverride}
                />
              </Box>
            </Flex>

            <Flex flexWrap="wrap" mb={[2, 2, 0]}>
              <Box width={[1, 1 / 2]} pr={[0, 2]} mb={[2, 3, 3]}>
                <WrappedTextInput
                  name="jobTitle"
                  label="Job Title"
                  errorMsg={errors.jobTitle}
                  touched={touched.jobTitle}
                  onBlur={onBlurOverride}
                  validator={(value) => validator.validate('jobTitle', { jobTitle: value })}
                />
              </Box>

              <Box width={[1, 1 / 2]} pl={[0, 2]}>
                <WrappedTextInput
                  name="email"
                  label="Email"
                  errorMsg={errors.email}
                  touched={touched.email}
                  onBlur={onBlurOverride}
                  validator={(value) => validator.validate('email', { email: value })}
                />
              </Box>
            </Flex>

            <Flex flexWrap="wrap" mb={[2, 2, 0]}>
              <Box width={[1, 1 / 2]} pr={[0, 2]} mb={[2, 3, 3]}>
                <WrappedTextInput
                  name="password"
                  label="Password"
                  type="password"
                  errorMsg={errors.password}
                  touched={touched.password}
                  onBlur={onBlurOverride}
                  validator={(value) => {
                    return validator.validate('password', { password: value }).then((result) => {
                      // The api doesn't support the full password criteria validation, so also check that afterward
                      return result || validatePasswordHandler(value);
                    });
                  }}
                />
              </Box>

              <Box width={[1, 1 / 2]} pl={[0, 2]}>
                <WrappedTextInput
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  errorMsg={errors.confirmPassword}
                  touched={touched.confirmPassword}
                  onBlur={onBlurOverride}
                  validator={(value) => passwordMatch(value, values.password)}
                />
              </Box>
            </Flex>
            <NextPrevButtons>
              <Button text="Next" size="large" type="submit" iconSuffix={<IconNext />} isLoading={isSubmitting} />
              <PrevButton onClick={() => onBack(values)}>Go Back</PrevButton>
            </NextPrevButtons>
          </form>
        );
      }}
    </Formik>
  );
};

ProfileForm.propTypes = {
  initialValues: PropTypes.shape({}), // TODO: fill out the shape
  onSubmit: PropTypes.func,
  onBack: PropTypes.func,
  validator: PropTypes.shape({
    validate: PropTypes.func,
  }),
  enableReinitialize: PropTypes.bool,
};

ProfileForm.defaultProps = {
  initialValues: {},
  onSubmit: () => {},
  validator: { validate: () => {} },
  enableReinitialize: true,
};

export { CompanyForm, ProfileForm };
